import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Always use relative /api path - works in both preview and production
// because the ingress/nginx routes /api to the backend on any domain
const API = '/api';

// Export for components that need it (image URLs, etc.)
// Empty string means relative URLs, which work on any domain
export { BACKEND_URL };

export const api = axios.create({
  baseURL: API,
});

// Public API instance (no auth interceptor)
export const publicAPI = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (token) => api.get(`/auth/verify/${token}`),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
};

export const usersAPI = {
  uploadAvatar: (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAvatar: (userId) => api.delete(`/users/${userId}/avatar`),
};

export const uploadAPI = {
  uploadImage: (file, imageType = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const servicesAPI = {
  getAll: (category) => api.get('/services', { params: { category } }),
  getById: (id) => api.get(`/services/${id}`),
  getBySlug: (slug) => api.get(`/services/by-slug/${slug}`),
  getCategories: () => api.get('/services/categories/list'),
  getRecommended: (id) => api.get(`/services/${id}/recommended`),
};

export const clientServicesAPI = {
  getMyServices: () => api.get('/client-services'),
  getServiceDetail: (id) => api.get(`/client-services/${id}`),
  toggleAutoRenewal: (id) => api.put(`/client-services/${id}/toggle-renewal`),
  cancelService: (id, reason) => api.post(`/client-services/${id}/cancel`, { reason }),
};

// Public APIs (no auth required) - uses publicAPI axios instance
// publicAPI is an axios instance defined at line 11

export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  getMyOrders: () => api.get('/orders'),
};

export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  createCustomerPortal: (returnUrl) => api.post('/payments/customer-portal', { return_url: returnUrl }),
};

export const kbAPI = {
  search: (query) => api.get('/kb/search', { params: { q: query } }),
  getArticle: (id) => api.get(`/kb/articles/${id}`),
};

export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

export const adminAPI = {
  // Users
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUserDetail: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  addUserTag: (id, tag) => api.post(`/admin/users/${id}/tags`, { tag }),
  removeUserTag: (id, tag) => api.delete(`/admin/users/${id}/tags/${tag}`),
  
  // Clients (separate from team)
  getAllClients: (params) => api.get('/admin/clients', { params }),
  getClientDetail: (id) => api.get(`/admin/clients/${id}`),
  
  // Client Notes
  getClientNotes: (clientId) => api.get(`/admin/clients/${clientId}/notes`),
  createClientNote: (clientId, note) => api.post(`/admin/clients/${clientId}/notes`, { note }),
  updateClientNote: (clientId, noteId, note) => api.put(`/admin/clients/${clientId}/notes/${noteId}`, { note }),
  deleteClientNote: (clientId, noteId) => api.delete(`/admin/clients/${clientId}/notes/${noteId}`),
  manageClientPayment: (clientId, returnUrl) => api.post(`/admin/clients/${clientId}/manage-payment`, { return_url: returnUrl }),
  
  // Groups
  getAllGroups: () => api.get('/admin/groups'),
  createGroup: (data) => api.post('/admin/groups', data),
  updateGroup: (id, data) => api.put(`/admin/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/admin/groups/${id}`),
  addUserToGroup: (groupId, userId) => api.post(`/admin/groups/${groupId}/users/${userId}`),
  removeUserFromGroup: (groupId, userId) => api.delete(`/admin/groups/${groupId}/users/${userId}`),
  
  // Team
  getTeamMembers: () => api.get('/admin/team'),
  createTeamMember: (data) => api.post('/admin/team', data),
  updateTeamMember: (id, data) => api.put(`/admin/team/${id}`, data),
  deleteTeamMember: (id) => api.delete(`/admin/team/${id}`),
  
  // Services
  createService: (data) => api.post('/admin/services', data),
  getAllServicesAdmin: () => api.get('/admin/services/all'),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  
  // Orders
  getAllOrders: (params) => api.get('/admin/orders', { params }),
  getOrderDetail: (id) => api.get(`/admin/orders/${id}`),
  updateOrderStatus: (id, status) => api.put(`/admin/orders/${id}/status`, { status }),
  deleteOrder: (id) => api.delete(`/admin/orders/${id}`),
  
  // KB
  createArticle: (data) => api.post('/admin/kb/articles', data),
  getAllArticles: () => api.get('/admin/kb/articles'),
  updateArticle: (id, data) => api.put(`/admin/kb/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/admin/kb/articles/${id}`),
  
  // Notifications (Admin)
  sendNotification: (data) => api.post('/admin/notifications/send', data),
  sendRenewalReminders: (daysBefore = 7) => api.post(`/admin/notifications/send-renewal-reminders?days_before=${daysBefore}`),
  getNotificationStats: () => api.get('/admin/notifications/stats'),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Integration Hub - Webhooks
  getWebhooks: () => api.get('/admin/integrations/webhooks'),
  getWebhookEvents: () => api.get('/admin/integrations/webhooks/events'),
  createWebhook: (data) => api.post('/admin/integrations/webhooks', data),
  updateWebhook: (id, data) => api.put(`/admin/integrations/webhooks/${id}`, data),
  deleteWebhook: (id) => api.delete(`/admin/integrations/webhooks/${id}`),
  testWebhook: (id) => api.post(`/admin/integrations/webhooks/${id}/test`),
  getWebhookLogs: (id, limit = 50) => api.get(`/admin/integrations/webhooks/${id}/logs`, { params: { limit } }),
  
  // Integration Hub - API Keys
  getApiKeys: () => api.get('/admin/integrations/api-keys'),
  createApiKey: (data) => api.post('/admin/integrations/api-keys', data),
  revokeApiKey: (id) => api.delete(`/admin/integrations/api-keys/${id}`),
  getSyncLogs: (params) => api.get('/admin/integrations/sync-logs', { params }),
  
  // Site Settings
  getSiteSettings: () => api.get('/admin/site-settings'),
  updateSiteSettings: (data) => api.put('/admin/site-settings', data),
  
  // AI Chatbot Config
  getAIConfig: () => api.get('/ai/admin/config'),
  updateAIConfig: (data) => api.put('/ai/admin/config', data),
  addQAPair: (data) => api.post('/ai/admin/config/qa', data),
  deleteQAPair: (index) => api.delete(`/ai/admin/config/qa/${index}`),
  getAIAnalytics: () => api.get('/ai/admin/analytics'),
  getAIFeedback: () => api.get('/ai/admin/feedback'),
  
  // Email Settings
  getEmailSettings: () => api.get('/admin/email-settings'),
  updateEmailSettings: (data) => api.put('/admin/email-settings', data),
  testEmailSettings: (data) => api.post('/admin/email-settings/test', data),
  resetEmailSettings: () => api.delete('/admin/email-settings'),
};

// AI Chatbot API (for users)
export const aiAPI = {
  sendMessage: (message, sessionId) => api.post('/ai/chat', { message, session_id: sessionId }),
  getSessions: () => api.get('/ai/chat/sessions'),
  getSessionHistory: (sessionId) => api.get(`/ai/chat/sessions/${sessionId}`),
  getCredits: () => api.get('/ai/credits'),
  getCreditPackages: () => api.get('/ai/credits/packages'),
  purchaseCredits: (packageId) => api.post('/ai/credits/purchase', { package_id: packageId }),
};

// Site settings API (uses publicAPI - no auth required)
export const siteAPI = {
  getSiteSettings: () => publicAPI.get('/site-settings'),
  submitContact: (data) => publicAPI.post('/contact', data),
  guestCheckout: (data) => publicAPI.post('/guest-checkout', data),
};

// Client API (generic authenticated requests)
export const clientAPI = {
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  delete: (url, config) => api.delete(url, config),
};
